import type { FC } from 'react';
import React, { useEffect, useState, useMemo, useCallback, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as ScenarioActions from '../../../actions/scenarios/scenarioAction.ts';
import * as CompaniesSelector from 'app/selectors/companiesSelector.ts';
import * as GuiStateSelectors from 'app/selectors/guiStateSelectors.ts';
import * as ScenarioSelectors from 'app/selectors/scenarioSelectors.ts';
import * as UserSelectors from 'app/selectors/userSelectors.ts';
import { ListItemText, ListItemIcon, Button, Switch, Collapse, ListItemButton, ListItem } from '@mui/material';

import AddScenarioSection from './AddScenarioSection/index.ts';
import useStyles, { StyledScenarioListItemButton } from './styles.ts';

import type { ScenarioDto } from '@/api-gen/api.schemas';

import ScenarioSearch from './ScenarioSearch/index.ts';
import { isUserAnyAdminSelector, isUserSuperAdminSelector } from 'app/selectors/userSelectors.ts';
import type { SceneWithCompanyName } from 'app/components/NavigationDrawer/ScenarioSection/NavigationDrawerScenarioList/types.ts';
import NavigationDrawerScenarioList from 'app/components/NavigationDrawer/ScenarioSection/NavigationDrawerScenarioList/NavigationDrawerScenarioList.tsx';
import logger from 'app/utils/logger.ts';
import { ExpandLess, ExpandMore, ViewList } from '@mui/icons-material';

import { useTranslation } from 'react-i18next';
import type { I18nScenarioSectionNs } from '@/i18n/dictionaries/interfaces.ts';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import { GuiStateActions } from 'app/actions/guiState/guiStateAction.ts';

const ScenarioSection: FC = () => {
  const [translate] = useTranslation(I18nNamespace.ScenarioSection);
  const classes = useStyles();
  const dispatch = useDispatch();
  const scenarios = useSelector(ScenarioSelectors.scenariosOrvalSelector);
  const companies = useSelector(CompaniesSelector.companiesSelector);
  const sideBarOpenState = useSelector(GuiStateSelectors.sideBarStateSelector);
  const isUserSuperAdmin = useSelector(isUserSuperAdminSelector);
  const currentUser = useSelector(UserSelectors.CurrentUserSelector);

  const [open, setOpen] = useState(false);
  const [searchWord, setSearchWord] = useState('');
  const [isShowGroupsEnabled, setIsShowGroupsEnabled] = useState(true);

  const scenesWithCompanyName = useMemo(() => {
    return scenarios
      .map((scene: ScenarioDto) => {
        if (!scene.companyId) {
          if (!isUserSuperAdmin && scene.sharedCompanies?.find((sc) => sc.id === currentUser.companyId)) {
            return { ...scene, companyName: currentUser.companyName };
          }

          return {
            ...scene,
            companyName: translate(nameof.full<I18nScenarioSectionNs>((l) => l.sharedScenarios)),
            companyId: 0
          };
        }
        const company = companies.find((company) => company.id === scene.companyId);
        return { ...scene, companyName: company?.name };
      })
      .filter((scenario) => !!scenario.companyName)
      .sort((a, b) => a.companyName.localeCompare(b.companyName));
  }, [companies, currentUser, isUserSuperAdmin, scenarios]);

  const handleClick = () => {
    if (!sideBarOpenState) {
      dispatch(GuiStateActions.sideMenuOpen(true));
      setOpen(true);
      return;
    }
    setOpen(!open);
  };

  const handleSelectScenario = () => {
    logger.log('dispatch was done!');
    dispatch(ScenarioActions.toNextLinkedScenario(false));
  };

  const filterScenes = useMemo((): Array<SceneWithCompanyName> => {
    if (!!searchWord && !searchWord.trim()) {
      return scenesWithCompanyName;
    }

    return scenesWithCompanyName.filter((x) => x.name.toLowerCase().indexOf(searchWord.toLocaleLowerCase()) >= 0);
  }, [searchWord, scenesWithCompanyName]);

  const search = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSearchWord(event.target.value);
  };

  const handleShowGroups = () => {
    setIsShowGroupsEnabled(!isShowGroupsEnabled);
  };

  const isAnyAdmin = useSelector(isUserAnyAdminSelector);

  return (<>
    <StyledScenarioListItemButton data-cy="scenarioOpenListButton" onClick={handleClick}>
      <ListItemIcon sx={{ minWidth: 24 }}>
        <ViewList classes={{ root: classes.black }} />
      </ListItemIcon>
      {sideBarOpenState && (<Fragment>
        <ListItemText primary={translate(nameof.full<I18nScenarioSectionNs>((l) => l.scenarios))} />
        {open ? <ExpandLess /> : <ExpandMore />}
      </Fragment>)}
    </StyledScenarioListItemButton>
    {sideBarOpenState && (
      <Collapse in={open} timeout="auto" unmountOnExit classes={{ entered: classes.colapsedEntered }}>
        {isAnyAdmin && (
          <Button fullWidth className={classes.switchLight} onClick={handleShowGroups}>
            <Switch size="small" checked={isShowGroupsEnabled} />
            {translate(nameof.full<I18nScenarioSectionNs>((l) => l.showFolders))}
          </Button>
        )}
        <ScenarioSearch search={search} />

        {isAnyAdmin && <AddScenarioSection />}

        <NavigationDrawerScenarioList
          filterScenes={filterScenes}
          isShowGroupsEnabled={isShowGroupsEnabled}
          handleSelectScenario={handleSelectScenario}
          searchWord={searchWord}
        />
      </Collapse>
    )}
  </>);
};

export default ScenarioSection;
